import { Apollo } from 'src/gql/generated';
import { Flag } from 'src/helpers/feature-flags';
import { z } from 'zod';

import type { MentionUserFilter } from '../types';

export const DEFAULT_PREVIEW_LIMIT = 5;
export const DEFAULT_DRILLDOWN_LIMIT = 25;

export const DEFAULT_ENABLED_TYPES = [
  Apollo.MentionEntitySearchType.User,
  Apollo.MentionEntitySearchType.Document,
  Apollo.MentionEntitySearchType.Playbook,
  ...(Flag.V2_MENTION_TYPES
    ? [Apollo.MentionEntitySearchType.Workflow, Apollo.MentionEntitySearchType.Report]
    : []),
];
export const DEFAULT_QUICK_TYPES = [Apollo.MentionEntitySearchType.User];

export type SearchResult =
  Apollo.SearchMentionEntitiesQuery['searchMentionEntities']['groups'][number]['results'][number];
export type SearchGroup =
  Apollo.SearchMentionEntitiesQuery['searchMentionEntities']['groups'][number];

export type MultiSelectSearchResult = Extract<
  SearchResult,
  { __typename: 'Document' | 'Playbook' | 'Report' | 'Workflow' }
>;

const MULTI_SELECT_SEARCH_TYPES = {
  [Apollo.MentionEntitySearchType.Document]: true,
  [Apollo.MentionEntitySearchType.Playbook]: true,
  [Apollo.MentionEntitySearchType.Report]: true,
  [Apollo.MentionEntitySearchType.User]: false,
  [Apollo.MentionEntitySearchType.Workflow]: true,
} as const satisfies Record<Apollo.MentionEntitySearchType, boolean>;

const MULTI_SELECT_TYPENAMES = {
  Document: true,
  Playbook: true,
  Report: true,
  User: false,
  Workflow: true,
} as const satisfies Record<SearchResult['__typename'], boolean>;

/** Drops the User results `filterUser` rejects; other result types pass through. */
export function filterUserResults(
  groups: SearchGroup[],
  filterUser: MentionUserFilter | undefined
): SearchGroup[] {
  if (!filterUser) return groups;
  return groups.map(group => ({
    ...group,
    results: group.results.filter(result => result.__typename !== 'User' || filterUser(result)),
  }));
}

export type PickerItem =
  | { key: string; kind: 'aline-ai' }
  | { key: string; kind: 'category'; type: Apollo.MentionEntitySearchType }
  | { key: string; kind: 'documents-empty' }
  | { disabled: boolean; email: string; key: string; kind: 'email' }
  | { key: string; kind: 'entity'; result: SearchResult }
  | { key: string; kind: 'intake-form' }
  | { key: string; kind: 'view-all'; type: Apollo.MentionEntitySearchType };

export function getPickerItems({
  activeType,
  allowAlineAiMention,
  allowEmailMentions,
  allowFileUpload,
  allowIntakeFormMention,
  enabledTypes,
  groups,
  query,
  quickTypes = DEFAULT_QUICK_TYPES,
}: {
  activeType: Apollo.MentionEntitySearchType | null;
  allowAlineAiMention: boolean;
  enabledTypes: Apollo.MentionEntitySearchType[];
  groups: SearchGroup[];
  query: string;
  allowEmailMentions?: boolean;
  allowFileUpload?: boolean;
  allowIntakeFormMention?: boolean;
  quickTypes?: Apollo.MentionEntitySearchType[];
}): PickerItem[] {
  const trimmedQuery = query.trim();
  if (activeType) {
    const group = groups.find(resultGroup => resultGroup.type === activeType);
    const searchItems = group?.results.map(result => getEntityPickerItem(result)) ?? [];
    if (activeType !== Apollo.MentionEntitySearchType.User) return searchItems;

    const emailItem = getEmailPickerItem({ allowEmailMentions, groups, query: trimmedQuery });
    return emailItem ? [...searchItems, emailItem] : searchItems;
  }

  if (!trimmedQuery) {
    const enabledQuickTypes = getEnabledQuickTypes({ enabledTypes, quickTypes });
    const quickTypeSet = new Set(enabledQuickTypes);
    const quickItems = enabledQuickTypes.flatMap(type => {
      const group = groups.find(resultGroup => resultGroup.type === type);
      if (getShowDocumentsEmptyItem({ allowFileUpload, group, type }))
        return [getDocumentsEmptyPickerItem()];

      return [
        ...(group?.results.map(result => getEntityPickerItem(result)) ?? []),
        ...(group?.pageInfo.hasMore ? [getViewAllPickerItem(type)] : []),
      ];
    });
    const categoryItems = enabledTypes
      .filter(type => !quickTypeSet.has(type))
      .map(type => getCategoryPickerItem(type));
    return [
      ...(allowAlineAiMention ? [getAlineAiPickerItem()] : []),
      ...quickItems,
      ...(allowIntakeFormMention ? [getIntakeFormPickerItem()] : []),
      ...categoryItems,
    ];
  }

  let insertedEmailItem = false;
  const emailItem = getEmailPickerItem({ allowEmailMentions, groups, query: trimmedQuery });
  const searchItems = groups.flatMap(group => {
    const entityItems = group.results.map(result => getEntityPickerItem(result));
    const viewAllItems = group.pageInfo.hasMore ? [getViewAllPickerItem(group.type)] : [];

    if (group.type !== Apollo.MentionEntitySearchType.User || !emailItem)
      return [...entityItems, ...viewAllItems];

    insertedEmailItem = true;
    return [...entityItems, emailItem, ...viewAllItems];
  });

  return emailItem && !insertedEmailItem ? [...searchItems, emailItem] : searchItems;
}

export function getSearchMentionEntitiesInput({
  activeType,
  documentId,
  enabledTypes,
  query,
  quickTypes = DEFAULT_QUICK_TYPES,
  workflowId,
}: {
  activeType: Apollo.MentionEntitySearchType | null;
  enabledTypes: Apollo.MentionEntitySearchType[];
  query: string;
  documentId?: string;
  quickTypes?: Apollo.MentionEntitySearchType[];
  workflowId?: string;
}): Apollo.SearchMentionEntitiesInput {
  if (activeType) {
    return {
      documentId,
      limit: DEFAULT_DRILLDOWN_LIMIT,
      query,
      types: [activeType],
      workflowId,
    };
  }

  const trimmedQuery = query.trim();

  return {
    documentId,
    limit: DEFAULT_PREVIEW_LIMIT,
    query,
    types: trimmedQuery ? enabledTypes : getEnabledQuickTypes({ enabledTypes, quickTypes }),
    workflowId,
  };
}

export function getMentionEntitySearchCursorInput(cursor: SearchGroup['pageInfo']['nextCursor']) {
  if (!cursor) return null;

  return {
    id: cursor.id,
    type: cursor.type,
  };
}

export function getDocumentMentionDisplayName(name: string) {
  return name || 'Untitled Document';
}

export function getIsMultiSelectSearchType(type: Apollo.MentionEntitySearchType) {
  return MULTI_SELECT_SEARCH_TYPES[type];
}

export function getIsMultiSelectSearchResult(
  result: SearchResult
): result is MultiSelectSearchResult {
  return MULTI_SELECT_TYPENAMES[result.__typename];
}

/**
 * Documents, playbooks, workflows, and reports can be checked off and inserted as a batch.
 * Users and non-entity rows (categories, email, Aline AI) keep single-select / navigate behavior.
 */
export function getMultiSelectResultForItem(item: PickerItem) {
  if (item.kind !== 'entity') return null;
  if (!getIsMultiSelectSearchResult(item.result)) return null;

  return item.result;
}

export function getSelectedEntityKey(result: MultiSelectSearchResult) {
  return `${result.__typename}:${result.id}`;
}

export function getSearchResultDisplayName(result: SearchResult) {
  switch (result.__typename) {
    case 'Document':
      return getDocumentMentionDisplayName(result.name);
    case 'Playbook':
      return result.title;
    case 'Report':
    case 'Workflow':
      return result.name;
    case 'User':
      return result.displayName;
  }
}

/** Adds or removes an entity from the pending selection, keeping the order it was checked in. */
export function toggleSelectedEntity(
  selectedEntities: MultiSelectSearchResult[],
  result: MultiSelectSearchResult
): MultiSelectSearchResult[] {
  const key = getSelectedEntityKey(result);
  if (selectedEntities.some(entity => getSelectedEntityKey(entity) === key))
    return selectedEntities.filter(entity => getSelectedEntityKey(entity) !== key);

  return [...selectedEntities, result];
}

const DRILLDOWN_PICKER_ITEM_KINDS = {
  'aline-ai': false,
  category: true,
  'documents-empty': false,
  email: false,
  entity: false,
  'intake-form': false,
  'view-all': true,
} as const satisfies Record<PickerItem['kind'], boolean>;

/**
 * Enter, Tab, and ArrowRight on a non-drill-down row commit a pending multi-select instead of
 * inserting only the highlighted entity. ArrowRight on a category / view-all row still drills
 * down so the selection can keep growing.
 */
export function getShouldCommitPendingSelection({
  highlightedItem,
  key,
  selectedCount,
}: {
  highlightedItem: PickerItem | undefined;
  key: string;
  selectedCount: number;
}) {
  if (!selectedCount) return false;
  if (key === 'Enter' || key === 'Tab') return true;
  if (key !== 'ArrowRight' || !highlightedItem) return false;

  return !DRILLDOWN_PICKER_ITEM_KINDS[highlightedItem.kind];
}

function getEnabledQuickTypes({
  enabledTypes,
  quickTypes,
}: {
  enabledTypes: Apollo.MentionEntitySearchType[];
  quickTypes: Apollo.MentionEntitySearchType[];
}) {
  const enabledTypeSet = new Set(enabledTypes);
  const enabledQuickTypes = quickTypes.filter(type => enabledTypeSet.has(type));
  if (enabledQuickTypes.length) return enabledQuickTypes;

  return enabledTypes.slice(0, 1);
}

/**
 * The Documents section only exists when it has items, so with no documents its header — and
 * the upload button that lives in that header — would disappear. A placeholder row keeps the
 * section (and the way to upload a first document) on screen. A missing group means the search
 * has not resolved yet, so hold off and let the loading skeletons show instead.
 */
function getShowDocumentsEmptyItem({
  allowFileUpload,
  group,
  type,
}: {
  type: Apollo.MentionEntitySearchType;
  allowFileUpload?: boolean;
  group?: SearchGroup;
}) {
  if (!allowFileUpload) return false;
  if (type !== Apollo.MentionEntitySearchType.Document) return false;
  if (!group) return false;

  return !group.results.length;
}

function getDocumentsEmptyPickerItem(): PickerItem {
  return {
    key: 'documents-empty',
    kind: 'documents-empty',
  };
}

function getAlineAiPickerItem(): PickerItem {
  return {
    key: 'aline-ai',
    kind: 'aline-ai',
  };
}

function getIntakeFormPickerItem(): PickerItem {
  return {
    key: 'intake-form',
    kind: 'intake-form',
  };
}

function getEntityPickerItem(result: SearchResult): PickerItem {
  return {
    key: `${result.__typename}:${result.id}`,
    kind: 'entity',
    result,
  };
}

function getCategoryPickerItem(type: Apollo.MentionEntitySearchType): PickerItem {
  return {
    key: `category:${type}`,
    kind: 'category',
    type,
  };
}

function getViewAllPickerItem(type: Apollo.MentionEntitySearchType): PickerItem {
  return {
    key: `view-all:${type}`,
    kind: 'view-all',
    type,
  };
}

function getEmailPickerItem({
  allowEmailMentions,
  groups,
  query,
}: {
  groups: SearchGroup[];
  query: string;
  allowEmailMentions?: boolean;
}): PickerItem | null {
  if (!allowEmailMentions) return null;

  const email = query.trim().replace(/^@/, '');
  if (!email) return null;

  const lowerEmail = email.toLowerCase();
  const isValidEmail = z.email().safeParse(email).success;
  const existingUser = groups
    .flatMap(group => group.results)
    .find(result => result.__typename === 'User' && result.email.toLowerCase() === lowerEmail);
  if (existingUser) return null;

  return {
    disabled: !isValidEmail,
    email,
    key: `email:${lowerEmail}`,
    kind: 'email',
  };
}
